import { projectService } from "@/app/providers/services";
import { useCurrentProject, useWorkspace } from "@/common";
import { useFullRelease } from "@/common/hooks_useFullRelease";
import { useNotify } from "@/common/notifications";
import {
  useBrowseReleasesModal,
  useExportReleaseModal,
  useImportReleaseModal,
  useReleaseModal,
  releaseDownloadJson,
} from "@/features/release";
import { useEditWorkspaceModal } from "@/features/workspace";
import { useRouting } from "@/hooks/use-routing";
import { useAddProjectModal, useEditProject } from "@/modals/ProjectModal";
import AddEntity from "@/pages/models/release/DataModel/AddEntity";
import { useExportProjectModal } from "@/pages/projects/ExportProjectModal";
import { useImportProjectModal } from "@/pages/projects/ImportProjectModal";
import { setProject, useScope } from "@/redux/scope";
import { useDialogModal } from "@/ui/dialogs/useDialogModal";
import type React from "react";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import paletteStyles from "../CommandPalette.module.scss";
import { useCommandPaletteContext } from "../CommandPaletteContext";
import { useDebouncedSearch, useRegisterCommands } from "../hooks";
import type { Command as CommandType } from "../types";

// ---------------------------------------------------------------------------
// Intent detection — extract a project name from rough natural language input
// ---------------------------------------------------------------------------

const CREATE_PROJECT_PATTERNS = [
  // "create project <name>", "new project <name>", "add project: <name>", etc.
  /^(?:create|new|add|make)\s+project\s*[:\-]?\s*(.+)/i,
  // "create <name> project", "new <name> project"
  /^(?:create|new|add|make)\s+(.+?)\s+project$/i,
  // bare "project <name>"
  /^project\s*[:\-]?\s+(.+)/i,
];

function extractProjectName(input: string): string | null {
  const trimmed = input.trim();
  for (const pattern of CREATE_PROJECT_PATTERNS) {
    const match = trimmed.match(pattern);
    if (match?.[1]?.trim()) return match[1].trim();
  }
  return null;
}

// ---------------------------------------------------------------------------
// Render component shown inside the palette item (like InlineCalcRenderer)
// ---------------------------------------------------------------------------

const InlineCreateProjectRenderer: React.FC = () => {
  const { searchValue } = useCommandPaletteContext();
  const name = useMemo(() => extractProjectName(searchValue) ?? "", [searchValue]);
  return (
    <div className={paletteStyles.itemContent}>
      <div className={paletteStyles.itemName}>
        Create new project <strong>"{name}"</strong>
      </div>
      <div className={paletteStyles.itemDescription}>Press Enter to create and open</div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// useQuickCreateProjectCommand — registers an inline create command whenever
// the debounced search value looks like a project-creation intent
// ---------------------------------------------------------------------------

const QUICK_CREATE_COMMAND_ID = "quick-create-project";

function useQuickCreateProjectCommand(pluginId: string): void {
  const dispatch = useDispatch();
  const { workspace } = useScope();
  const { close } = useCommandPaletteContext();
  const notify = useNotify();
  const { goToPage } = useRouting();
  const debouncedSearch = useDebouncedSearch(150);
  const { mutateAsync } = projectService.useCreate();

  const projectName = useMemo(() => extractProjectName(debouncedSearch), [debouncedSearch]);

  const inlineCommands = useMemo((): CommandType[] => {
    if (!projectName) return [];
    return [
      {
        id: QUICK_CREATE_COMMAND_ID,
        label: `Create new project "${projectName}"`,
        description: "Create and open this project",
        keywords: ["experimental"],
        category: "Projects",
        scoreOverride: 9998,
        render: InlineCreateProjectRenderer,
        primary: {
          id: `${QUICK_CREATE_COMMAND_ID}-action`,
          label: `Create "${projectName}"`,
          action: async () => {
            try {
              const result = await mutateAsync({
                name: projectName,
                workspace,
                description: "",
                tags: [],
                visibility: "public",
                locale: "en-au",
                locked: false,
              } as any);
              projectService.invalidateAllQueries();
              dispatch(setProject(result.id, { resetRelease: true }));
              close();
              goToPage("/build");
              notify.success(`Project "${projectName}" created`);
            } catch {
              notify.error(`Failed to create project "${projectName}"`);
            }
          },
        },
      },
    ];
  }, [projectName, workspace, mutateAsync, dispatch, close, notify, goToPage]);

  useRegisterCommands({ pluginId, commands: inlineCommands });
}

/**
 * Returns `{ releaseId, releaseRef }` so `useMemo` can depend on the primitive
 * `releaseId` instead of the full Release object. The ref stays current so
 * action closures always read the latest data without triggering unnecessary
 * recomputes when the saga re-fetches the release.
 */
function useStableRelease() {
  const release = useFullRelease();
  const releaseRef = useRef(release);
  releaseRef.current = release;
  return { releaseId: release?.id, releaseRef };
}

// ---------------------------------------------------------------------------
// CommandsPlugin - Provides quick access to common modal actions
// ---------------------------------------------------------------------------

/**
 * CommandsPlugin - Provides quick access to common modal actions
 *
 * This plugin provides commands for:
 * - Opening the Import Project modal
 * - Opening the Create Project modal
 * - Opening the Edit Project modal
 * - Opening the Edit Workspace modal
 * - Opening the Create Entity modal
 * - Quick-create a project directly from the search input (inline)
 */
export const CommandsPlugin: React.FC = () => {
  const notify = useNotify();
  const { t } = useTranslation();
  const { workspace: workspaceId } = useScope();
  const currentWorkspace = useWorkspace(workspaceId);
  const project = useCurrentProject();
  const { releaseId, releaseRef: fullReleaseRef } = useStableRelease();
  const openImportProject = useImportProjectModal();
  const openExportProject = useExportProjectModal();
  const openAddProject = useAddProjectModal();
  const openEditProject = useEditProject();
  const openEditWorkspace = useEditWorkspaceModal();
  const openReleaseCenter = useBrowseReleasesModal();
  const openImportRelease = useImportReleaseModal();
  const openReleaseModal = useReleaseModal();
  const openExportRelease = useExportReleaseModal();
  const { close } = useCommandPaletteContext();
  const { openDialogModal } = useDialogModal();

  // Quick-create project from inline search input
  useQuickCreateProjectCommand("commands-plugin-quick-create");

  // Define commands
  const commands: CommandType[] = useMemo(() => {
    const cmds: CommandType[] = [
      {
        id: "import-project",
        label: t("projects.import_label", "Import Project"),
        description: "Import a project from a file",
        category: "Projects",
        keywords: ["import", "project", "upload", "file"],
        primary: {
          id: "import-project-action",
          label: "Open Import Project Modal",
          action: () => {
            openImportProject();
            close();
          },
        },
      },
      {
        id: "create-project",
        label: t("projects.create_label", "Create Project"),
        description: "Create a new project",
        category: "Projects",
        keywords: ["create", "new", "project", "add"],
        primary: {
          id: "create-project-action",
          label: "Open Create Project Modal",
          action: () => {
            openAddProject();
            close();
          },
        },
      },
    ];

    if (currentWorkspace) {
      cmds.push({
        id: "edit-workspace",
        label: t("workspaces.edit", "Edit Workspace"),
        description: "Edit the current workspace",
        category: "Workspaces",
        keywords: ["edit", "workspace", "update", "settings", "current"],
        primary: {
          id: "edit-workspace-action",
          label: "Open Edit Workspace Modal",
          action: () => {
            openEditWorkspace(currentWorkspace);
            close();
          },
        },
      });
    }

    if (project) {
      cmds.push(
        {
          id: "edit-project",
          label: t("projects.edit", "Edit Project"),
          description: "Edit the current project",
          category: "Projects",
          keywords: ["edit", "project", "update", "settings", "current"],
          primary: {
            id: "edit-project-action",
            label: "Open Edit Project Modal",
            action: () => {
              openEditProject(project);
              close();
            },
          },
        },
        {
          id: "export-project",
          label: t("projects.export_label", "Export Project"),
          description: "Export the current project",
          category: "Projects",
          keywords: ["export", "project", "download", "backup"],
          primary: {
            id: "export-project-action",
            label: "Open Export Project Modal",
            action: () => {
              openExportProject(project);
              close();
            },
          },
        },
        {
          id: "release-center",
          label: t("release_centre.title", "Release Center"),
          description: "Browse and manage releases for the current project",
          category: "Releases",
          keywords: ["release", "releases", "center", "centre", "browse", "manage"],
          primary: {
            id: "release-center-action",
            label: "Open Release Center",
            action: () => {
              openReleaseCenter(project.id);
              close();
            },
          },
        },
        {
          id: "import-release",
          label: t("release_centre.import_release", "Import Release"),
          description: "Import a release into the current project",
          category: "Releases",
          keywords: ["release", "import", "upload", "project"],
          primary: {
            id: "import-release-action",
            label: "Open Import Release Modal",
            action: () => {
              openImportRelease(project.id);
              close();
            },
          },
        },
      );
    }

    // Only add create entity command if we have a current release
    if (releaseId) {
      cmds.push(
        {
          id: "edit-release",
          label: t("release_centre.edit_release", "Edit Release"),
          description: "Edit the current release",
          category: "Releases",
          keywords: ["release", "edit", "update", "current"],
          primary: {
            id: "edit-release-action",
            label: "Open Edit Release Modal",
            action: () => {
              openReleaseModal(fullReleaseRef.current!, "edit");
              close();
            },
          },
        },
        {
          id: "new-release-command",
          label: t("release_centre.new_release", "New Release"),
          description: "Create a new release from the current release",
          category: "Releases",
          keywords: ["release", "new", "create", "duplicate", "clone", "version"],
          primary: {
            id: "new-release-command-action",
            label: "Open New Release Modal",
            action: () => {
              openReleaseModal(fullReleaseRef.current!);
              close();
            },
          },
        },
        {
          id: "export-release",
          label: t("release_centre.export_release", "Export Release"),
          description: "Export the current release",
          category: "Releases",
          keywords: ["release", "export", "download", "backup"],
          primary: {
            id: "export-release-action",
            label: "Open Export Release Modal",
            action: () => {
              openExportRelease(fullReleaseRef.current!);
              close();
            },
          },
        },
      );

      cmds.push({
        id: "download-raw-release",
        label: "Download Raw Release",
        description: "Download the full release as a JSON file",
        category: "Releases",
        keywords: ["release", "download", "raw", "json", "export", "debug"],
        primary: {
          id: "download-raw-release-action",
          label: "Download JSON",
          action: () => {
            const release = fullReleaseRef.current;
            try {
              const blob = new Blob([releaseDownloadJson(release)], { type: "application/json" });
              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              link.download = `${release?.model_name || release?.model}-R${release?.releaseNo || release?.versionNo}-release.json`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
              notify.info("Release JSON downloaded");
            } catch {
              notify.error("Failed to download release JSON");
            }
            close();
          },
        },
      });

      cmds.push(
        {
          id: "create-entity",
          label: t("entities.create_label", "Create Entity"),
          description: "Create a new entity in the current release",
          category: "Data Model",
          keywords: ["create", "new", "entity", "add", "data", "model"],
          primary: {
            id: "create-entity-action",
            label: "Open Create Entity Modal",
            action: () => {
              openDialogModal(
                { title: t("entities.add_entity", "Add entity"), maxWidth: "sm" },
                ({ close: closeDialog }) => (
                  <AddEntity
                    release={fullReleaseRef.current}
                    onOpenChange={(open) => !open && closeDialog()}
                  />
                ),
              );
              close();
            },
          },
        },
      );
    }

    return cmds;
  }, [
    t,
    currentWorkspace,
    project,
    openImportProject,
    openExportProject,
    openAddProject,
    openEditProject,
    openEditWorkspace,
    openReleaseCenter,
    openImportRelease,
    openReleaseModal,
    openExportRelease,
    releaseId,
    notify,
    close,
    openDialogModal,
  ]);

  // Register commands with the palette
  useRegisterCommands({
    pluginId: "commands-plugin",
    commands,
  });

  // Plugin doesn't render anything
  return null;
};
